import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { resolveAvatarThumbUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';

const GRID_ROWS_PER_PAGE = 4;
const SEARCH_GRID_BREAKPOINTS = {
  mobile: 767.84,
  twoCols: 959.98,
  threeCols: 1279.98,
  fourCols: 1679.98
};

function getSearchGridColumns(width) {
  if (width <= SEARCH_GRID_BREAKPOINTS.mobile) return 1;
  if (width <= SEARCH_GRID_BREAKPOINTS.twoCols) return 2;
  if (width <= SEARCH_GRID_BREAKPOINTS.threeCols) return 3;
  if (width <= SEARCH_GRID_BREAKPOINTS.fourCols) return 4;
  return 5;
}

const INTERESTS_MAP = {
  'путешествия': 101,
  'кино и сериалы': 102,
  'музыка и концерты': 103,
  'чтение и литература': 104,
  'видеоигры и гейминг': 105,
  'спорт и активный отдых': 106,
  'кулинария и гастрономия': 107,
  'другое': 108
};

function Search() {
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const [items, setItems] = useState([]);
  const [likeAnimations, setLikeAnimations] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [gridColumns, setGridColumns] = useState(() => (
    typeof window === 'undefined' ? 5 : getSearchGridColumns(window.innerWidth)
  ));
  
  // Filter states
  const [filterMode, setFilterMode] = useState('search'); // 'search' | 'city' | 'age' | 'hobby' | 'profession'
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef(null);
  const loadMoreRef = useRef(null);
  const currentQueryRef = useRef('');
  const currentFilterRef = useRef('search');

  // Close filter menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    function updateGridColumns() {
      setGridColumns(getSearchGridColumns(window.innerWidth));
    }

    updateGridColumns();
    window.addEventListener('resize', updateGridColumns);
    return () => window.removeEventListener('resize', updateGridColumns);
  }, []);

  const getPlaceholder = () => {
    switch(filterMode) {
      case 'city': return 'Введите город';
      case 'age': return 'Введите возраст';
      case 'hobby': return 'Введите хобби';
      case 'profession': return 'Введите профессию';
      default: return 'Введите имя или никнейм';
    }
  };

    const mergeUniqueById = (current, incoming) => {
    const seen = new Set(current.map((x) => x.id));
    const merged = [...current];
    for (const item of incoming) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        merged.push(item);
      }
    }
    return merged;
  };

  const pageSize = gridColumns * GRID_ROWS_PER_PAGE;

  const fetchPage = useCallback(async ({ query = '', mode = 'search', cursor = null, append = false }) => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/log_in';
      return;
    }

    const q = query ? query.trim() : '';
    const params = new URLSearchParams({ count: String(pageSize) });
    if (cursor) params.set('cursor', cursor);

    if (q) {
      switch (mode) {
        case 'city':
          params.set('city', q);
          break;
        case 'age': {
          const age = parseInt(q, 10);
          if (!isNaN(age)) {
            params.set('minAge', age);
            params.set('maxAge', age);
          }
          break;
        }
        case 'profession':
          params.set('jobTitle', q);
          break;
        case 'hobby': {
          const lowerQ = q.toLowerCase();
          const matchedIds = [];
          for (const [key, id] of Object.entries(INTERESTS_MAP)) {
            if (key.includes(lowerQ)) matchedIds.push(id);
          }
          if (matchedIds.length > 0) {
            params.set('hobbies', matchedIds.join(','));
          }
          break;
        }
        case 'search':
        default:
          params.set('q', q);
          break;
      }
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      const resp = await fetch(`${API_BASE}/api/Matching/feed?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (resp.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/log_in';
        return;
      }

      if (!resp.ok) throw new Error('fetch failed');
      const data = await resp.json();
      const pageItems = Array.isArray(data.items) ? data.items : [];
      const pageCursor = data?.nextCursor || null;
      const mapped = pageItems.map((item) => ({
        ...item,
        likedByMe: Boolean(item.isLikedByMe || item.likedByMe || item.isMutual)
      }));

      setItems((prev) => (append ? mergeUniqueById(prev, mapped) : mapped));
      setNextCursor(pageCursor);
      setHasMore(Boolean(pageCursor));
    } catch (e) {
      setError('Не удалось загрузить результаты поиска');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [pageSize]);

  const load = useCallback((query) => {
    const q = query ? query.trim() : '';
    currentQueryRef.current = q;
    currentFilterRef.current = filterMode;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    fetchPage({ query: q, mode: filterMode, cursor: null, append: false });
  }, [fetchPage, filterMode]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || !nextCursor) return;
    fetchPage({
      query: currentQueryRef.current,
      mode: currentFilterRef.current,
      cursor: nextCursor,
      append: true
    });
  }, [fetchPage, hasMore, loading, loadingMore, nextCursor]);

  useEffect(() => {
    currentQueryRef.current = '';
    currentFilterRef.current = 'search';
    fetchPage({ query: '', mode: 'search', cursor: null, append: false });
  }, [fetchPage]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [items.length, loadMore]);

  const handleSearch = () => load(searchInput);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const shouldShowFooter = !onboardingLoading && (!loading || items.length > 0 || Boolean(error));

  const toggleLike = async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) {
        window.location.href = '/log_in';
        return;
    }

    const currentItem = items.find((item) => item.id === userId);
    if (!currentItem) return;

    const nextLiked = !currentItem.likedByMe;

    setItems((prev) => prev.map((item) => (
      item.id === userId ? { ...item, likedByMe: nextLiked } : item
    )));
    setLikeAnimations((prev) => ({
      ...prev,
      [userId]: nextLiked ? 'like' : 'unlike'
    }));
    
    try {
        const resp = await fetch(`${API_BASE}/api/Likes/like/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
        });
    
        if (resp.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/log_in';
        return;
        }
    
        if (!resp.ok) {
          throw new Error('like failed');
        }
        const data = await resp.json();
    
        if (data && typeof data.liked === 'boolean') {
          setItems((prev) => prev.map((item) => (
            item.id === userId ? { ...item, likedByMe: data.liked } : item
          )));
        }
    } catch (e) {
        setItems((prev) => prev.map((item) => (
          item.id === userId ? { ...item, likedByMe: currentItem.likedByMe } : item
        )));
        setLikeAnimations((prev) => ({
          ...prev,
          [userId]: currentItem.likedByMe ? 'like' : 'unlike'
        }));
    }
  };

  return (
    <div className="search-page min-h-screen flex flex-col font-['TT_Norms_Pro']" style={{ backgroundColor: 'var(--search-page-bg)' }}>

      <main className="search-page__main flex-grow mx-auto">
        {/* Back Button */}
        <div className="search-page__back md:mt-0">
          <button onClick={() => navigate(-1)} className="search-page__back-button">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-0">
                      <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Назад
          </button>
        </div>

        {/* Search Bar */}
        <div className="search-page__search relative z-10">
          <div className="search-page__search-inner flex items-center w-full">
            <i className="search-page__search-icon bi bi-search flex-shrink-0" style={{ color: 'var(--search-icon-muted)' }}></i>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              className="search-page__input flex-grow h-full outline-none font-tt-norms min-w-0"
            />
            
            {/* Filter Button */}
            <div className="search-page__filter-wrap" ref={filterMenuRef}>
                <button
                  className="search-page__filter rounded-full border-2 flex items-center justify-center transition-colors"
                  style={{
                    borderColor: 'var(--search-filter-border)',
                    backgroundColor: showFilterMenu ? 'var(--search-filter-bg-active)' : 'var(--search-filter-bg)'
                  }}
                  onClick={() => setShowFilterMenu(!showFilterMenu)}
                >
                  <svg className="search-page__filter-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 4V14" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <path d="M6 18V20" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <circle cx="6" cy="16" r="2" stroke="var(--search-filter-icon)" strokeWidth="1"/>
                    
                    <path d="M12 4V6" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <path d="M12 10V20" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <circle cx="12" cy="8" r="2" stroke="var(--search-filter-icon)" strokeWidth="1"/>
                    <path d="M18 4V14" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <path d="M18 18V20" stroke="var(--search-filter-icon)" strokeWidth="1" strokeLinecap="round"/>
                    <circle cx="18" cy="16" r="2" stroke="var(--search-filter-icon)" strokeWidth="1"/>
                  </svg>
                </button>
                
                {/* Filter Popup Menu */}
                {showFilterMenu && (
                  <div className="search-page__filter-menu text-left">
                    <div className="search-page__filter-options">
                         {[
                           { key: 'city', label: 'По городу' },
                           { key: 'age', label: 'По возрасту' },
                           { key: 'hobby', label: 'По хобби' },
                           { key: 'profession', label: 'По профессии' }
                         ].map(opt => (
                           <button
                              key={opt.key}
                              type="button"
                              className="search-page__filter-option"
                              onClick={() => {
                                setFilterMode((currentMode) => (
                                  currentMode === opt.key ? 'search' : opt.key
                                ));
                              }}
                            >
                              <div className="search-page__filter-radio">
                                {filterMode === opt.key ? (
                                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
                                    <circle cx="6.5" cy="6.5" r="4.8" stroke="var(--search-card-icon-accent)" strokeWidth="3"/>
                                    <circle cx="6.5" cy="6.5" r="2" fill="var(--search-card-icon-accent)"/>
                                  </svg>
                                ) : (
                                   <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible' }}>
                                    <circle cx="6.5" cy="6.5" r="5.3" stroke="var(--search-filter-radio)" strokeWidth="2"/>
                                   </svg>
                                )}
                              </div>
                              <span className="search-page__filter-option-text">{opt.label}</span>
                           </button>
                         ))}
                    </div>
                  </div>
                )}
            </div>

            <button
              className="search-page__submit transition-colors flex-shrink-0"
              style={{ backgroundColor: 'var(--color-primary)', color: 'var(--registration-button-text)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--search-submit-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary)'; }}
              onClick={handleSearch}
            >
              Поиск
            </button>
          </div>
        </div>

        {loading && items.length === 0 && (
          <div className="w-full max-w-[1200px] mb-6 text-center" style={{ color: 'var(--search-muted)' }}>Загрузка...</div>
        )}

        {!loading && error && (
          <div className="w-full max-w-[1200px] mb-6 text-center" style={{ color: 'var(--search-muted)' }}>{error}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="w-full max-w-[1200px] mb-6 text-center" style={{ color: 'var(--search-muted)' }}>Ничего не найдено</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="search-page__grid grid">
            {items.map(user => {
              const fullNameForAlt = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.nickname || 'Без имени';
              const username = user.nickname ? (user.nickname.startsWith('@') ? user.nickname : '@' + user.nickname) : '';
              
              // Construct details string: "Town, 21 yo, Job"
              const detailsParts = [];
              if (user.city) detailsParts.push(user.city);
              if (typeof user.age === 'number') detailsParts.push(`${user.age} лет`); 
              if (user.jobTitle) detailsParts.push(user.jobTitle);
              
              return (
                <div key={user.id} onClick={() => navigate(`/user/${user.id}`)} className="search-page__card overflow-hidden flex flex-col group transition-shadow w-full cursor-pointer" style={{ backgroundColor: 'var(--search-card-bg)' }}>
                  {/* Photo */}
                  <div className="search-page__photo aspect-square overflow-hidden relative" style={{ backgroundColor: 'var(--search-photo-bg)' }}>
                    <img
                      src={resolveAvatarThumbUrl(user.avatarUrl, 512, 'cover')}
                      onError={withAvatarFallback()}
                      alt={fullNameForAlt}
                      loading="lazy"
                      decoding="async"
                      style={{ objectPosition: '50% 22%' }}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="search-page__card-body flex flex-col flex-grow items-start text-left">
                     {/* Name */}
                     <div className="search-page__name w-full break-words">
                       {user.firstName && <div>{user.firstName}</div>}
                       {user.lastName && <div>{user.lastName}</div>}
                       {(!user.firstName && !user.lastName) && (user.nickname || 'Без имени')}
                     </div>

                     {username && (
                        <div className="search-page__username w-full truncate" title={username}>{username}</div>
                     )}
                     
                     {/* Details */}
                     <p
                         className="search-page__details flex-grow opacity-90"
                         title={detailsParts.join(', ')}
                     >
                         {detailsParts.join(', ')}
                     </p>

                     {/* Bottom Icons Row */}
                     <div className="search-page__card-bottom flex items-center justify-between w-full">
                          <div className="search-page__stats">
                              {/* Friends/Person Icon */}
                              <div className="search-page__stat">
                                 <svg className="search-page__stat-icon translate-y-[0.5px]" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <mask id="mask0_1836_3811" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="-1" width="23" height="24">
                                       <rect y="-0.00012207" width="22.7234" height="22.7234" fill="var(--search-card-icon-mask)"/>
                                    </mask>
                                    <g mask="url(#mask0_1836_3811)">
                                       <path d="M1.70312 16.1756C1.70312 15.6877 1.82905 15.2522 2.0809 14.8692C2.33275 14.4862 2.66911 14.1883 3.08996 13.9754C3.98975 13.5353 4.8945 13.196 5.80423 12.9574C6.71411 12.719 7.71402 12.5997 8.80395 12.5997C9.89405 12.5997 10.894 12.719 11.8037 12.9574C12.7136 13.196 13.6184 13.5353 14.5182 13.9754C14.939 14.1883 15.2754 14.4862 15.5272 14.8692C15.7791 15.2522 15.905 15.6877 15.905 16.1756V16.8421C15.905 17.2232 15.7651 17.5582 15.4853 17.8472C15.2056 18.1361 14.866 18.2806 14.4666 18.2806H3.14133C2.74209 18.2806 2.40258 18.1407 2.1228 17.8609C1.84302 17.581 1.70312 17.2414 1.70312 16.8421V16.1756ZM19.5831 18.2806H17.4327C17.5517 18.0633 17.6424 17.832 17.7049 17.5868C17.7674 17.3416 17.7986 17.0934 17.7986 16.8421V16.0591C17.7986 15.4377 17.6465 14.8454 17.3423 14.2822C17.0379 13.7192 16.6061 13.2361 16.047 12.8329C16.6819 12.9276 17.2846 13.0741 17.8552 13.2724C18.4257 13.471 18.9701 13.7055 19.4885 13.9762C19.9776 14.2372 20.3554 14.5448 20.6218 14.8991C20.8882 15.2532 21.0213 15.6399 21.0213 16.0591V16.8421C21.0213 17.2414 20.8814 17.581 20.6017 17.8609C20.3219 18.1407 19.9824 18.2806 19.5831 18.2806ZM8.80395 11.0702C7.89265 11.0702 7.11256 10.7457 6.46368 10.0968C5.8148 9.44781 5.49036 8.66764 5.49036 7.75633C5.49036 6.84503 5.8148 6.06494 6.46368 5.41606C7.11256 4.76702 7.89265 4.4425 8.80395 4.4425C9.71526 4.4425 10.4954 4.76702 11.1445 5.41606C11.7933 6.06494 12.1178 6.84503 12.1178 7.75633C12.1178 8.66764 11.7933 9.44781 11.1445 10.0968C10.4954 10.7457 9.71526 11.0702 8.80395 11.0702ZM16.9792 7.75633C16.9792 8.66764 16.6547 9.44781 16.0059 10.0968C15.357 10.7457 14.5769 11.0702 13.6656 11.0702C13.5587 11.0702 13.4228 11.058 13.2577 11.0337C13.0925 11.0094 12.9566 10.9827 12.8499 10.9537C13.2233 10.5048 13.5102 10.0067 13.7108 9.45964C13.9112 8.91255 14.0114 8.34446 14.0114 7.75539C14.0114 7.16616 13.9091 6.6002 13.7046 6.05752C13.5001 5.515 13.2152 5.01556 12.8499 4.5592C12.9858 4.5106 13.1217 4.47904 13.2577 4.46452C13.3936 4.44984 13.5296 4.4425 13.6656 4.4425C14.5769 4.4425 15.357 4.76702 16.0059 5.41606C16.6547 6.06494 16.9792 6.84503 16.9792 7.75633ZM3.1231 16.8604H14.4848V16.1756C14.4848 15.9779 14.4353 15.8019 14.3364 15.6477C14.2376 15.4936 14.0808 15.3588 13.8658 15.2435C13.0866 14.8417 12.2843 14.5373 11.4588 14.3303C10.6333 14.1234 9.74839 14.0199 8.80395 14.0199C7.85967 14.0199 6.9748 14.1234 6.14934 14.3303C5.32388 14.5373 4.52154 14.8417 3.74231 15.2435C3.52739 15.3588 3.37046 15.4936 3.27151 15.6477C3.17257 15.8019 3.1231 15.9779 3.1231 16.1756V16.8604ZM8.80395 9.64995C9.3247 9.64995 9.77049 9.46453 10.1413 9.0937C10.5122 8.72287 10.6976 8.27708 10.6976 7.75633C10.6976 7.23559 10.5122 6.7898 10.1413 6.41897C9.77049 6.04813 9.3247 5.86272 8.80395 5.86272C8.28321 5.86272 7.83742 6.04813 7.46659 6.41897C7.09575 6.7898 6.91034 7.23559 6.91034 7.75633C6.91034 8.27708 7.09575 8.72287 7.46659 9.0937C7.83742 9.46453 8.28321 9.64995 8.80395 9.64995Z" fill="var(--search-card-icon-accent)"/>
                                    </g>
                                 </svg>

                                 <span className="search-page__stat-count">{user.friendsCount || 0}</span>
                              </div>
                              
                              {/* Photos/Images Icon */}
                              <div className="search-page__stat">
                                 <svg className="search-page__stat-icon" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <mask id="mask0_1836_3816" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="-1" width="23" height="24">
                                       <rect y="-0.00012207" width="22.7234" height="22.7234" fill="var(--search-card-icon-mask)"/>
                                    </mask>
                                    <g mask="url(#mask0_1836_3816)">
<path d="M8.49048 15.9876C8.00407 15.9876 7.59236 15.819 7.25536 15.4816C6.91836 15.1443 6.74985 14.7322 6.74985 14.2453V4.01408C6.74985 3.52719 6.91836 3.11507 7.25536 2.77774C7.59236 2.4404 8.00407 2.27173 8.49048 2.27173H18.7115C19.198 2.27173 19.6097 2.4404 19.9467 2.77774C20.2837 3.11507 20.4522 3.52719 20.4522 4.01408V14.2453C20.4522 14.7322 20.2837 15.1443 19.9467 15.4816C19.6097 15.819 19.198 15.9876 18.7115 15.9876H8.49048ZM8.49048 14.5419H18.7115C18.798 14.5419 18.869 14.5141 18.9246 14.4585C18.9801 14.403 19.0079 14.3319 19.0079 14.2453V4.01408C19.0079 3.92749 18.9801 3.85641 18.9246 3.80083C18.869 3.74525 18.798 3.71746 18.7115 3.71746H8.49048C8.40398 3.71746 8.33297 3.74525 8.27744 3.80083C8.22192 3.85641 8.19415 3.92749 8.19415 4.01408V14.2453C8.19415 14.3319 8.22192 14.403 8.27744 14.4585C8.33297 14.5141 8.40398 14.5419 8.49048 14.5419ZM5.47045 20.4359C4.99014 20.4941 4.55893 20.3778 4.17684 20.087C3.79458 19.7961 3.57456 19.4107 3.51679 18.9309L2.28745 8.98523C2.22936 8.50444 2.35188 8.07225 2.65503 7.68865C2.95801 7.30489 3.34957 7.08216 3.82972 7.02048L4.06322 7.01108C4.26799 6.98152 4.45093 7.03686 4.61205 7.1771C4.77317 7.31734 4.85373 7.49661 4.85373 7.71491C4.85373 7.89691 4.79106 8.05619 4.66573 8.19273C4.54056 8.32927 4.38842 8.4124 4.20933 8.44212L4.0093 8.45127C3.92296 8.45754 3.85508 8.49304 3.80565 8.55778C3.75622 8.62267 3.73769 8.69841 3.75004 8.785L4.97601 18.7456C4.98837 18.8322 5.02536 18.9001 5.08698 18.9495C5.14877 18.9989 5.22283 19.0175 5.30917 19.0051L16.6823 17.5984C16.8871 17.5724 17.068 17.6197 17.2251 17.7403C17.3822 17.8608 17.4728 18.0236 17.4969 18.2285C17.5229 18.4337 17.4756 18.6108 17.3551 18.7601C17.2347 18.9095 17.0722 18.9961 16.8674 19.0201L5.47045 20.4359Z" fill="var(--search-card-icon-accent)"/>
</g>
</svg>

                                 <span className="search-page__stat-count">{user.photosCount || 1}</span>
                              </div>
                          </div>
                          
                          {/* Like Heart */}
                          <button 
                             className={`search-page__like-button bg-transparent border-none cursor-pointer flex items-center ${
                               likeAnimations[user.id] ? `is-${likeAnimations[user.id]}` : ''
                             }`}
                             onClick={(e) => { e.stopPropagation(); toggleLike(user.id); }}
                             onAnimationEnd={() => {
                               setLikeAnimations((prev) => {
                                 if (!prev[user.id]) return prev;
                                 const next = { ...prev };
                                 delete next[user.id];
                                 return next;
                               });
                             }}
                          >
                              <svg className="search-page__like-icon" width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<path
  className={`search-page__like-fill ${user.likedByMe ? 'is-filled' : ''}`}
  d="M10.7627 2.39844C8.05366 -0.274822 3.20222 0.704711 1.47559 3.94141C0.645736 5.49712 0.503029 7.67294 1.9209 10.3584C3.3114 12.9919 6.20149 16.0907 11.3613 19.4346C16.5208 16.0912 19.4113 12.9928 20.8018 10.3594C22.2196 7.67408 22.0759 5.49819 21.2461 3.94238C19.5195 0.705388 14.669 -0.274697 11.96 2.39844L11.3613 2.98828L10.7627 2.39844Z"
  fill="var(--search-card-icon-accent)"
/>
<path
  d="M10.7627 2.39844C8.05366 -0.274822 3.20222 0.704711 1.47559 3.94141C0.645736 5.49712 0.503029 7.67294 1.9209 10.3584C3.3114 12.9919 6.20149 16.0907 11.3613 19.4346C16.5208 16.0912 19.4113 12.9928 20.8018 10.3594C22.2196 7.67408 22.0759 5.49819 21.2461 3.94238C19.5195 0.705388 14.669 -0.274697 11.96 2.39844L11.3613 2.98828L10.7627 2.39844Z"
  stroke="var(--search-card-icon-accent)"
  strokeWidth="1.70426"
  fill="none"
/>
</svg>

                          </button>
                     </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && items.length > 0 && hasMore && (
          <div ref={loadMoreRef} className="w-full h-10" />
        )}

        {loadingMore && (
          <div className="w-full max-w-[1200px] my-4 text-center" style={{ color: 'var(--search-muted)' }}>Загрузка...</div>
        )}

        {!loading && !loadingMore && !error && hasMore && (
          <div className="w-full flex justify-center mb-6">
            <button
              type="button"
              onClick={loadMore}
              className="px-6 py-2.5 rounded-[8px] text-[15px] font-bold font-tt-norms border"
              style={{
                backgroundColor: 'var(--search-load-more-bg)',
                color: 'var(--search-load-more-text)',
                borderColor: 'var(--search-load-more-border)'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--search-load-more-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--search-load-more-bg)'; }}
            >
              Показать еще
            </button>
          </div>
        )}
      </main>

      {shouldShowFooter && <Footer />}
    </div>
  );
}

export default Search;


